import { useState } from "react";
import { Link } from "react-router-dom";
import useDocumentTitle from "@/hooks/useDocumentTitle";

const FORM_ADDR = "https://readdy.ai/api/form/db93u3s6oi3g3tv8usf0";

export default function About() {
  useDocumentTitle("关于我们 - 诱人的飞行");
  const [status, setStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = String(formData.get("website_alt") || "").trim();
    if (honeypot) {
      // Silently treat as normal submission for bots
      setStatus("success");
      form.reset();
      return;
    }
    formData.delete("website_alt");

    setStatus("submitting");
    setFormError("");

    try {
      const params = new URLSearchParams();
      formData.forEach((value, key) => {
        params.append(key, String(value));
      });

      const response = await fetch(FORM_ADDR, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: params.toString(),
      });

      const responseText = await response.text();
      let parsed: { code?: string; message?: string; meta?: { message?: string; detail?: string } } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || "";
      const lower = `${serverMsg} ${responseText}`.toLowerCase();

      if (response.ok && parsed?.code === "OK" && !lower.includes("spam")) {
        setStatus("success");
        form.reset();
      } else {
        setStatus("error");
        setFormError(serverMsg || "提交失败，请稍后重试。");
      }
    } catch {
      setStatus("error");
      setFormError("网络异常，提交失败，请稍后重试。");
    }
  };

  return (
    <div className="w-full">
      {/* 页头 */}
      <div className="w-full bg-primary-900 py-10 md:py-16 px-4 md:px-8">
        <nav className="text-xs text-background-300 mb-3 flex items-center gap-1.5">
          <Link to="/" className="hover:text-accent-400 transition-colors">首页</Link>
          <i className="ri-arrow-right-s-line" aria-hidden="true"></i>
          <span className="text-background-100">关于我们</span>
        </nav>
        <h1 className="text-2xl md:text-4xl font-heading font-bold text-background-50 mb-3">关于诱人的飞行</h1>
        <p className="text-sm md:text-base text-background-300 max-w-2xl leading-relaxed">
          我们把电影和电视剧里最重要的部分——故事，认真讲给你听。
        </p>
      </div>

      <div className="w-full px-4 md:px-8 py-10 md:py-16">
        {/* 定位与服务 */}
        <section className="mb-12 md:mb-16">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
            <div className="lg:col-span-5">
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-4 flex items-center gap-2">
                <span className="w-1 h-6 rounded bg-accent-500"></span>
                网站定位
              </h2>
            </div>
            <div className="lg:col-span-7">
              <p className="text-sm md:text-base text-foreground-700 leading-loose mb-4">
                「诱人的飞行」是一个围绕影视内容与剧情介绍展开的内容平台。我们关注最新上映的电影与热播剧集，
                为每一部作品整理清晰的基本信息、完整的剧情简介与类型标签，帮助你在打开播放器之前，
                就已经知道自己即将进入一个怎样的世界。
              </p>
              <p className="text-sm md:text-base text-foreground-700 leading-loose">
                我们不提供影片播放与下载，也不做资源聚合。我们只做一件事：让好的故事被更好地理解。
              </p>
            </div>
          </div>
        </section>

        {/* 服务内容 */}
        <section className="mb-12 md:mb-16">
          <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-6 flex items-center gap-2">
            <span className="w-1 h-6 rounded bg-accent-500"></span>
            我们提供什么
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
            {[
              { icon: "ri-movie-2-line", title: "最新内容", desc: "持续跟踪近期上线的电影与剧集，第一时间整理成卡片与详情。" },
              { icon: "ri-book-open-line", title: "剧情介绍", desc: "用完整而克制的文字，讲清楚每个故事的来龙去脉与情绪落点。" },
              { icon: "ri-filter-3-line", title: "多维筛选", desc: "按类型、地区、年份筛选，快速从海量作品中找到想看的那一部。" },
              { icon: "ri-gallery-line", title: "专题策划", desc: "定期推出主题片单，把气质相近的作品放在一起集中推荐。" },
            ].map((s) => (
              <div key={s.title} className="p-5 md:p-6 rounded-lg bg-background-100 border border-background-200">
                <span className="w-12 h-12 flex items-center justify-center rounded-md bg-primary-100 text-primary-600 mb-4">
                  <i className={`${s.icon} text-2xl`} aria-hidden="true"></i>
                </span>
                <h3 className="text-base font-medium text-foreground-950 mb-2">{s.title}</h3>
                <p className="text-sm text-foreground-600 leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </div>
        </section>

        {/* 团队 */}
        <section className="mb-12 md:mb-16">
          <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-6 flex items-center gap-2">
            <span className="w-1 h-6 rounded bg-accent-500"></span>
            团队介绍
          </h2>
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
            <div className="lg:col-span-7">
              <p className="text-sm md:text-base text-foreground-700 leading-loose mb-4">
                「诱人的飞行」由一支热爱影视的编辑团队运营。我们当中既有写影评多年的人，
                也有只想安静追完一部剧的普通观众。我们相信，好的推荐不需要夸张的形容词，
                只需要把故事本身讲得足够清楚。
              </p>
              <p className="text-sm md:text-base text-foreground-700 leading-loose">
                每一篇剧情介绍、每一份专题片单，都由编辑逐字整理与核对。我们希望这个网站能成为你选片时，
                那个安静而可靠的参考。
              </p>
            </div>
            <div className="lg:col-span-5">
              <div className="w-full h-[240px] md:h-[280px] rounded-lg overflow-hidden border border-background-200">
                <img
                  src="https://readdy.ai/api/search-image?query=warm%20workspace%20of%20a%20small%20editorial%20team%20at%20dusk%2C%20deep%20blue%20ambient%20light%20with%20warm%20orange%20desk%20lamps%2C%20posters%20of%20movies%20on%20wall%2C%20artistic%20photography&width=900&height=620&seq=fly-about-team&orientation=landscape"
                  alt="诱人的飞行编辑团队工作场景"
                  title="诱人的飞行 编辑团队 影视内容"
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>
          </div>
        </section>

        {/* 联系方式 + 留言表单 */}
        <section>
          <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-6 flex items-center gap-2">
            <span className="w-1 h-6 rounded bg-accent-500"></span>
            联系我们
          </h2>
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
            <div className="lg:col-span-5">
              <div className="flex flex-col gap-4">
                {[
                  { icon: "ri-mail-line", label: "电子邮箱", value: "hello@yourenfeixing.example" },
                  { icon: "ri-map-pin-line", label: "内容合作", value: "欢迎影视资讯与专题合作垂询" },
                  { icon: "ri-time-line", label: "回复时间", value: "工作日 10:00 - 18:00" },
                ].map((c) => (
                  <div key={c.label} className="flex items-start gap-4 p-5 rounded-lg bg-background-100 border border-background-200">
                    <span className="w-10 h-10 flex items-center justify-center rounded-md bg-accent-100 text-accent-700 shrink-0">
                      <i className={`${c.icon} text-lg`} aria-hidden="true"></i>
                    </span>
                    <div>
                      <p className="text-sm font-medium text-foreground-950 mb-1">{c.label}</p>
                      <p className="text-sm text-foreground-600 break-all">{c.value}</p>
                    </div>
                  </div>
                ))}
                <p className="text-xs text-foreground-500 leading-relaxed">
                  <i className="ri-information-line mr-1" aria-hidden="true"></i>
                  以上联系方式为演示信息，留言表单为本站唯一可用的联系渠道。
                </p>
              </div>
            </div>

            <div className="lg:col-span-7">
              <form
                id="contact-form"
                data-readdy-form
                onSubmit={handleSubmit}
                className="p-6 md:p-8 rounded-lg bg-background-50 border border-background-200"
              >
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-800 mb-2">称呼</label>
                    <input
                      id="contact-name"
                      name="name"
                      type="text"
                      required
                      placeholder="怎么称呼你"
                      className="w-full h-11 px-3 rounded-md bg-background-100 border border-background-200 text-sm text-foreground-900 placeholder:text-foreground-400 outline-none focus:border-primary-400 transition-colors"
                    />
                  </div>
                  <div>
                    <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-800 mb-2">邮箱</label>
                    <input
                      id="contact-email"
                      name="email"
                      type="email"
                      required
                      placeholder="name@example.com"
                      className="w-full h-11 px-3 rounded-md bg-background-100 border border-background-200 text-sm text-foreground-900 placeholder:text-foreground-400 outline-none focus:border-primary-400 transition-colors"
                    />
                  </div>
                </div>

                <div className="mb-4">
                  <label htmlFor="contact-subject" className="block text-sm font-medium text-foreground-800 mb-2">主题</label>
                  <select
                    id="contact-subject"
                    name="subject"
                    className="w-full h-11 px-3 rounded-md bg-background-100 border border-background-200 text-sm text-foreground-900 outline-none focus:border-primary-400 transition-colors cursor-pointer"
                  >
                    <option value="内容建议">内容建议</option>
                    <option value="专题合作">专题合作</option>
                    <option value="影视资讯投稿">影视资讯投稿</option>
                    <option value="其他">其他</option>
                  </select>
                </div>

                <div className="mb-4">
                  <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-800 mb-2">留言内容</label>
                  <textarea
                    id="contact-message"
                    name="message"
                    required
                    maxLength={500}
                    rows={5}
                    placeholder="想看的影视、想说的建议，都可以写在这里（最多 500 字）"
                    className="w-full px-3 py-3 rounded-md bg-background-100 border border-background-200 text-sm text-foreground-900 placeholder:text-foreground-400 outline-none focus:border-primary-400 transition-colors resize-none"
                  ></textarea>
                </div>

                {/* Anti-spam honeypot */}
                <input
                  type="text"
                  name="website_alt"
                  className="form-field-guard"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                />

                <button
                  type="submit"
                  disabled={status === "submitting"}
                  className="w-full sm:w-auto px-6 py-3 rounded-md bg-primary-500 hover:bg-primary-600 disabled:opacity-60 text-background-50 text-sm font-medium transition-colors whitespace-nowrap cursor-pointer flex items-center justify-center gap-2"
                >
                  {status === "submitting" ? (
                    <>
                      <i className="ri-loader-4-line animate-spin" aria-hidden="true"></i>
                      提交中
                    </>
                  ) : (
                    <>
                      <i className="ri-send-plane-line" aria-hidden="true"></i>
                      提交留言
                    </>
                  )}
                </button>

                {status === "success" && (
                  <div className="mt-4 p-3 rounded-md bg-accent-100 border border-accent-200 text-sm text-accent-900 flex items-center gap-2">
                    <i className="ri-checkbox-circle-line" aria-hidden="true"></i>
                    留言已提交，感谢你的反馈！
                  </div>
                )}
                {status === "error" && (
                  <div className="mt-4 p-3 rounded-md bg-secondary-200 border border-secondary-300 text-sm text-foreground-800 flex items-center gap-2">
                    <i className="ri-error-warning-line" aria-hidden="true"></i>
                    {formError || "提交失败，请稍后重试。"}
                  </div>
                )}
              </form>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}